import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { useFormChangeHandler } from "hooks/useFormChangeHandler";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useCreateData } from "hooks/useApiHooks";
import { formatNumber, unformatNumber } from "utils/utils";

const CreatePageReduceStock = ({ initialData, onSuccess, onError, suppliers }) => {
  const API_URL = "/menu";

  const [formData, setFormData] = useState({
    supplier_id: "",
    stock: "",
    note: "",
  });

  const { createData } = useCreateData(
    `${API_URL}/${initialData?.id}/stock`, // POST to /menu/{id}/stock
    "menus"
  );

  const handleChange = useFormChangeHandler(setFormData);

  const { handleSubmit, isSubmitting, formError } = useSubmitHandler(
    createData,
    onSuccess,
    onError
  );

  const handleBeforeSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      stock: unformatNumber(formData.stock), // Unformat the stock value before sending it
    };

    handleSubmit(payload, e);
  };

  // Format value when focus is lost (e.g., -1,000,000)
  const handleBlur = () => {
    if (formData.stock) {
      const formattedValue = formatNumber(formData.stock); // Format number for display
      setFormData({
        ...formData,
        stock: formattedValue,
      });
    }
  };

  // Convert formatted value back to raw number when focus is regained (e.g., -1000000)
  const handleFocus = () => {
    const rawValue = unformatNumber(formData.stock || ""); // Ensure no undefined or null value
    setFormData({
      ...formData,
      stock: rawValue,
    });
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
      });
    }
  }, [initialData]);

  return (
    <form
      onSubmit={handleBeforeSubmit}
      className="d-flex flex-column"
      style={{ height: "100%" }}
    >
      <div className="action-sheet-content flex-grow-1">
        <div className="row mb-3 align-items-center">
          <label
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            Reduce Stock:
          </label>
          <div className="col-sm-8">
            <input
              type="text" // Using text to handle formatting
              className={clsx(
                "form-control gotra-form-control rounded-1",
                formError?.stock && "is-invalid"
              )}
              id="stock"
              placeholder="-100"
              name="stock"
              value={formData.stock}
              onChange={handleChange}
              onBlur={handleBlur} // Format value when focus is lost
              onFocus={handleFocus} // Convert value back to raw number when focused
              disabled={isSubmitting}
              style={{ fontSize: "14px", height: "35px" }}
              required
              min={-1} // Ensuring only negative values can be input for reduction
            />
            {formError?.stock && (
              <div className="invalid-feedback">{formError.stock}</div>
            )}
          </div>
        </div>

        {/* Select Supplier */}
        <div className="row mb-3 align-items-center">
          <label
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            Supplier:
          </label>
          <div className="col-sm-8">
            <select
              className={clsx(
                "form-select gotra-form-control rounded-1",
                formError?.supplier_id && "is-invalid"
              )}
              id="supplier_id"
              name="supplier_id"
              value={formData.supplier_id}
              onChange={handleChange}
              disabled={isSubmitting}
              style={{ fontSize: "14px", height: "35px" }}
            >
              <option value="">Select Supplier</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
            {formError?.supplier_id && (
              <div className="invalid-feedback">{formError.supplier_id}</div>
            )}
          </div>
        </div>

        {/* Note Input */}
        <div className="row mb-3 align-items-center">
          <label
            className="col-sm-4 col-form-label d-flex justify-content-end"
            style={{ fontSize: "14px" }}
          >
            Note:
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className={clsx(
                "form-control gotra-form-control rounded-1",
                formError?.note && "is-invalid"
              )}
              id="note"
              placeholder="Note"
              name="note"
              value={formData.note}
              onChange={handleChange}
              disabled={isSubmitting}
              style={{ fontSize: "14px", height: "35px" }}
            />
            {formError?.note && (
              <div className="invalid-feedback">{formError.note}</div>
            )}
          </div>
        </div>
      </div>

      <div className="form-group mt-auto d-grid">
        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={isSubmitting}
        >
          Reduce Stock
        </button>
      </div>
    </form>
  );
};

export default CreatePageReduceStock;
